<template>
    <VJoyDialog
        data-testid="vetting-change-redirect-dialog"
        aria-labelledby="vettingChangeRedirectDialog"
        open
        size="large"
        @dialog:hide="emits('cancel')"
    >
        <template #dialog-header>
            <div id="confirmDialogTitle">
                <p>{{ $t(`profile.vetting.community.vetting.decision.redirect.dialog.title`, [getFullName]) }}</p>
                <p class="joy-font-primary-300 joy-mt-2">
                    {{ $t(`profile.vetting.community.vetting.decision.redirect.dialog.subtitle`) }}
                </p>
            </div>
        </template>

        <template #dialog-body>
            <div>
                <template v-if="loading">
                    <VJoyWrapper>
                        <VJoySpinner />
                        {{ $t('profile.vetting.community.loading') }}
                    </VJoyWrapper>
                </template>
                <template v-else-if="!loading && reasons">
                    <form :id="formId">
                        <p class="joy-font-primary-450">
                            {{ $t('profile.vetting.community.vetting.decision.redirect.dialog.body.title') }}
                        </p>
                        <VJoySelect
                            id="reason"
                            v-model="r$.$value.reason"
                            data-testid="vetting-change-redirect-dialog-reason"
                            :invalid="r$.reason.$error"
                        >
                            <option selected value="" disabled>
                                {{ $t('profile.vetting.community.vetting.decision.redirect.dialog.reason.placeholder') }}
                            </option>
                            <option v-for="reason in reasons" :key="reason.reasonForRefusal" :value="reason.reasonForRefusal">
                                {{ $t(`profile.vetting.community.vetting.decision.redirect.dialog.reason.${reason.reasonForRefusal}`) }}
                            </option>
                        </VJoySelect>
                        <VJoyFormError v-if="r$.reason.$error" class="joy-mt-4" :errors="r$.reason.$errors" />
                        <VJoySelect
                            id="reason-language"
                            v-model="r$.$value.language"
                            data-testid="vetting-change-redirect-dialog-language"
                            class="joy-mt-4"
                            :invalid="r$.language.$error"
                        >
                            <option selected value="" disabled>
                                {{ $t('profile.vetting.community.vetting.decision.redirect.dialog.language.placeholder') }}
                            </option>
                            <option v-for="language in getLanguageToEmailTemplatesInner" :key="language.language" :value="language.language">
                                {{ language.language }}
                            </option>
                        </VJoySelect>
                        <VJoyFormError v-if="r$.language.$error" class="joy-mt-4" :errors="r$.language.$errors" />
                        <VJoyLabel class="joy-mt-4">{{ $t(`profile.vetting.community.vetting.decision.redirect.dialog.body.subject`) }}</VJoyLabel>
                        <VJoyInput
                            v-model="r$.$value.emailSubject"
                            data-testid="vetting-change-redirect-dialog-email-subject"
                            name="subject"
                            :placeholder="$t('profile.vetting.community.vetting.decision.redirect.dialog.body.subject.placeholder')"
                            :invalid="r$.emailSubject.$error"
                        />
                        <VJoyFormError v-if="r$.emailSubject.$error" class="joy-mt-4" :errors="r$.emailSubject.$errors" />

                        <VJoyLabel class="joy-mt-4">{{ $t(`profile.vetting.community.vetting.decision.redirect.dialog.body.message`) }}</VJoyLabel>
                        <VJoyTextarea
                            v-model="r$.$value.emailBody"
                            :placeholder="$t('profile.vetting.community.vetting.decision.redirect.dialog.body.message.placeholder')"
                            :invalid="r$.emailBody.$error"
                            data-testid="vetting-change-redirect-dialog-text-area"
                        />
                        <VJoyFormError v-if="r$.emailBody.$error" class="joy-mt-4" :errors="r$.emailBody.$errors" />

                        <VJoyLabel class="joy-mt-4">
                            {{ $t(`profile.vetting.community.vetting.decision.redirect.dialog.footer.message`) }}
                        </VJoyLabel>
                        <!-- eslint-disable-next-line vue/no-v-html -->
                        <span v-html="r$.$value.emailFooter"></span>
                    </form>
                </template>
                <template v-else-if="!loading && error">
                    {{ error }}
                </template>
            </div>
        </template>

        <template #dialog-cancel>
            <VJoyButton
                :disabled="loading"
                variant="ghost"
                :loading="!!freelancerVerticalStore.loading"
                :label="$t('profile.vetting.community.vetting.decision.dialog.cancel.cta')"
                @click="emits('cancel')"
            />
        </template>
        <template #dialog-confirm>
            <VJoySubmit
                data-testid="vetting-change-redirect-dialog-submit"
                :form="formId"
                :disabled="loading"
                variant="main"
                :loading="!!freelancerVerticalStore.loading"
                :action="handleConfirm"
            >
                {{ $t('profile.vetting.community.vetting.decision.redirect.dialog.confirm.cta') }}
            </VJoySubmit>
        </template>
    </VJoyDialog>
</template>

<script setup lang="ts">
    import {computed, reactive, watch} from 'vue';
    import {minLength, required} from '@regle/rules';
    import {storeToRefs} from 'pinia';
    import {VJoySubmit} from '#components';
    import {useRegle, validateForm} from '@malt/forms';
    import {VJoyButton, VJoyDialog, VJoyLabel, VJoyTextarea, VJoyFormError, VJoyInput, VJoySelect, VJoySpinner, VJoyWrapper} from '@maltjoy/core-vue';
    import type {LanguageToEmailTemplatesInner, ReasonForRefusal, StrategyRefusalReasonsInner} from '@api';
    import {useRedirectReasons} from '~/composables/useRedirectReason';
    import {useFreelancerVerticalStore} from '@/store/freelancerVerticalStore';
    import {useProfileQualificationStore} from '@/store/profileQualificationStore';
    import {useProfileStore} from '@/store/profileStore';

    const {profile} = useProfileStore();
    const freelancerVerticalStore = useFreelancerVerticalStore();
    const {freelancerVerticalState} = storeToRefs(freelancerVerticalStore);

    const profileQualificationStore = useProfileQualificationStore();
    const {qualification} = storeToRefs(profileQualificationStore);

    const emits = defineEmits<{
        (e: 'cancel'): void;
        (e: 'confirm'): void;
    }>();

    const {error, loading, reasons} = useRedirectReasons(profile!.profileId);
    const formId = 'vetting-change-redirect-dialog-form';

    const state = reactive<{
        reason: ReasonForRefusal | undefined;
        language: string | undefined;
        emailSubject: string | undefined;
        emailBody: string | undefined;
        emailFooter: string | undefined;
    }>({
        reason: undefined,
        language: undefined,
        emailSubject: undefined,
        emailBody: undefined,
        emailFooter: undefined,
    });

    const {r$} = useRegle(state, {
        reason: {
            required,
        },
        language: {
            required,
        },
        emailSubject: {
            required,
            minLength: minLength(3),
        },
        emailBody: {
            required,
            minLength: minLength(3),
        },
    });

    const getSelectedReason = computed((): StrategyRefusalReasonsInner | undefined =>
        reasons.value?.find((item) => item.reasonForRefusal === state.reason),
    );

    const getFullName = computed(() => `${profile?.firstname} ${profile?.lastname}`);

    const getLanguageToEmailTemplatesInner = computed(
        (): LanguageToEmailTemplatesInner[] | undefined => getSelectedReason.value?.languageToEmailTemplates,
    );

    watch(
        () => state.reason,
        () => {
            state.language = qualification.value.language ?? 'en';
            updateState();
        },
    );

    watch(
        () => state.language,
        () => updateState(),
    );

    function updateState() {
        const languageTemplate = getLanguageToEmailTemplatesInner.value?.find((language) => language.language === state.language);
        if (languageTemplate) {
            state.emailSubject = languageTemplate.template.subject;
            state.emailBody = languageTemplate.template.body;
            state.emailFooter = languageTemplate.template.footer;
        }
    }

    async function handleConfirm() {
        const {valid, data} = await validateForm(r$, {formId});
        if (!valid) {
            return;
        }
        await freelancerVerticalStore.refuseToStrategy(data.reason!, data.language!, data.emailSubject!, data.emailBody!);
        if (freelancerVerticalState.value.length) {
            emits('confirm');
        }
    }
</script>
